import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import fileToArrayBuffer from 'file-to-array-buffer';
import shp from 'shpjs';
import uuid4 from 'uuid/v4';
import { fromJS, OrderedMap } from 'immutable';
import { graphql } from 'react-apollo';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { featureCollection as turfFeatureCollection } from '@turf/helpers';
import turfArea from '@turf/area';
import turfBbox from '@turf/bbox';
import turfFlatten from '@turf/flatten';
import { polygon as turfPolygon } from '@turf/helpers';
import turfPointOnFeature from '@turf/point-on-feature';

import { FETCH_ALL_FIELDS } from 'api/field';

import { history } from 'store';
import { BannerComponent, ErrorBoundary, LevelNav } from 'common/components';
import {
  bboxToBounds,
  latLngToCoords,
  sqMetersToAcres,
} from 'common/components/MapComponent/util';

import ChooseFieldsForm from './ChooseFieldsForm';
import Map from './Map';
import UploadForm from './UploadForm';

import './index.css';

export const MAX_FEATURES_ON_MAP = 100;
export const MAX_INITIAL_CHECKED_FEATURES = 10;

// Convert from "shape" (list of coordinates describing a polygon) to a Geojson
// polygon feature
const shapeToGeojson = (shape, properties = {}, options = {}) =>
  turfPolygon(
    [[...shape.map(latLngToCoords), latLngToCoords(shape[0])]],
    properties,
    options
  );

// Transformer for imported files
const fileGetter = async event => {
  const fileList = event.dataTransfer
    ? event.dataTransfer.files
    : event.target.files;

  const filePromises = Array.from(fileList).map(async file => {
    // Load file into a buffer
    const buffer = await fileToArrayBuffer(file);

    // Convert to geojson
    let geojsons;
    try {
      geojsons = await shp(buffer);
    } catch (error) {
      return file;
    }

    // Normalize output
    if (!Array.isArray(geojsons)) {
      geojsons = [geojsons];
    }

    // Merge all features into a single FeatureCollection, turn any
    // MultiPolygons into Polygons, then extract the list of features
    const allFeatures = turfFlatten(
      turfFeatureCollection([].concat(...geojsons.map(fc => fc.features)))
    ).features;

    // Filter to Polygon and MultiPolygon features
    const features = allFeatures
      .filter(
        feature =>
          feature.type === 'Feature' &&
          ['Polygon', 'MultiPolygon'].includes(feature.geometry.type)
      )
      // Map to the data we want
      .map(feature => {
        const area = sqMetersToAcres(turfArea(feature));
        return {
          id: uuid4(),
          name: feature.properties.name || feature.id || '',
          area,
          geojson: feature,
          point: turfPointOnFeature(feature),
        };
      });

    Object.defineProperty(file, 'features', {
      value: features,
    });

    return file;
  });

  return Promise.all(filePromises);
};

// Various defaults
export const DEFAULT_AREA_THRESHOLD = [20, Infinity];
export const DEFAULT_FEATURES = OrderedMap();
export const DEFAULT_IMPORT_ERROR = false;

// Main component
const FieldUpload = ({
  dispatch,
  existingFieldsLoading,
  existingFieldsError,
  existingFields,
}) => {
  const [areaThreshold, setAreaThreshold] = useState(DEFAULT_AREA_THRESHOLD);
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [importError, setImportError] = useState(DEFAULT_IMPORT_ERROR);
  const mapRef = useRef(null);

  const areaInRange = area =>
    area >= areaThreshold[0] && area <= areaThreshold[1];

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'application/zip,.zip',
    getFilesFromEvent: fileGetter,
    maxSize: 2 * 1024 * 1024 * 1024,
    onDrop: acceptedFiles => {
      // Filter out files with no features found
      acceptedFiles = acceptedFiles.filter(
        file => file.features && file.features.length
      );

      if (!acceptedFiles.length) {
        setFeatures(DEFAULT_FEATURES);
        setImportError(true);
        return;
      }

      let i = 0;
      const features = fromJS(
        acceptedFiles
          // Flatten features from each input file into single list
          .reduce((acc, file) => [...acc, ...file.features], [])

          // Add state flags
          .map(feature => ({
            ...feature,

            // The checked flag is true when the feature's list row's checkbox
            // is checked
            checked: false,

            // The hovering is true when the feature's list row or the
            // depiction on the map is being hovered
            hovering: false,

            // The editingArea flag is true when the area input field is
            // focused, since we don't want it to suddenly disappear during
            // editing: eg 300 -> 30 -> 305 -- if the lower threshold is 100,
            // it'd disappear during that edit
            editingArea: false,
          }))

          // Key by ID for easy access
          .reduce((acc, feature) => {
            acc[feature.id] = feature;
            return acc;
          }, {})
      )
        // Sort by area descending
        // Note that the sort is not done again when areas are edited; this is
        // on purpose and is to reduce confusion during editing. We could add a
        // manual sort button or perhaps sort on blur if necessary. Probably not
        // needed since any edits to area should be very minor for real-world
        // situations.
        .sortBy(feature => -feature.get('area'))

        // Check the largest n features in the area range
        .map(feature =>
          feature.set(
            'checked',
            i++ < MAX_INITIAL_CHECKED_FEATURES &&
              areaInRange(feature.get('area'))
          )
        );

      setFeatures(features);
      setImportError(false);

      // Zoom to all loaded features
      if (mapRef.current) {
        mapRef.current.fitBounds(
          bboxToBounds(
            turfBbox(
              turfFeatureCollection(
                features
                  .map(feature => feature.get('geojson'))
                  .valueSeq()
                  .toJS()
              )
            )
          )
        );
      }
    },
  });

  // Filtered features
  const filteredFeatures = features.filter(
    feature => feature.get('editingArea') || areaInRange(feature.get('area'))
  );

  return (
    <ErrorBoundary>
      <React.Fragment>
        <BannerComponent theme={['relative', 'strong']}>
          <LevelNav
            nav={{
              left: {
                label: <span className={'visible--desktop'}>Back</span>,
                action: () => history.goBack(),
              },
              center: {
                label: 'Import fields',
              },
            }}
          />
        </BannerComponent>
        <div className="columns is-gapless">
          <aside className="column is-one-third field-wrapper__aside uploadfield__column">
            <div className="column__inner uploadfield__leftpanel">
              {features.size === 0 ? (
                <UploadForm
                  getInputProps={getInputProps}
                  getRootProps={getRootProps}
                  importError={importError}
                />
              ) : (
                <ChooseFieldsForm
                  areaInRange={areaInRange}
                  areaThreshold={areaThreshold}
                  dispatch={dispatch}
                  existingFields={existingFields}
                  features={features}
                  filteredFeatures={filteredFeatures}
                  mapRef={mapRef}
                  setAreaThreshold={setAreaThreshold}
                  setFeatures={setFeatures}
                  setImportError={setImportError}
                />
              )}
            </div>
          </aside>

          {/* Map component */}
          {existingFieldsLoading || existingFieldsError ? null : (
            <Map
              existingFields={existingFields}
              features={features}
              filteredFeatures={filteredFeatures}
              mapRef={mapRef}
              setFeatures={setFeatures}
            />
          )}
        </div>
      </React.Fragment>
    </ErrorBoundary>
  );
};

FieldUpload.propTypes = {
  dispatch: PropTypes.func,
  existingFields: PropTypes.arrayOf(PropTypes.object),
  existingFieldsLoading: PropTypes.bool,
  existingFieldsError: PropTypes.object,
};

export default compose(
  connect(),
  graphql(FETCH_ALL_FIELDS, {
    options: {
      fetchPolicy: 'cache-and-network',
      variables: {
        active: true,
      },
    },
    props: ({ loading, error, data }) => ({
      existingFieldsLoading: loading,
      existingFieldsError: error,
      existingFields: (data.getAllFields || [])
        .map(field => {
          const ret = { ...field };
          try {
            ret.geojson = field.geojson
              ? JSON.parse(field.geojson)
              : shapeToGeojson(field.shape);
          } catch (error) {
            // Corrupted shape, eg a polygon with only two points
            return null;
          }
          return ret;
        })
        .filter(field => field != null),
    }),
  })
)(FieldUpload);



// WEBPACK FOOTER //
// ./src/pages/Field/Upload/index.js